import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Select as AntDSelect, Tooltip, SelectProps as AntDSelectProps } from 'antd';
import useQueryApiClient, { RequestMethod } from 'utils/useApiClient';
import { useT } from 'hooks/Locale/useT';
import debounce from 'lodash.debounce';
import { constructValidationRules } from './validations';
import { ComponentAccessCheck } from 'ui';
import './styles.scss';
import { useLocaleContext } from 'context/LocaleContext';
import generalHelpers from 'utils/generalHelpers';

interface OptionType {
    value: string | number;
    label: string;
    [key: string]: any;
}

interface SelectProps extends AntDSelectProps {
    url?: string;
    params?: Record<string, any>;
    integerKey?: boolean;
    dataKey?: string;
    isResponseArray?: boolean;
    valueKey?: string;
    labelKey?: string;
    incrementKey?: boolean;
    mapDataEntries?: (item: any) => OptionType;
    name?: string;
    label?: string;
    validation?: Record<string, any>;
    customRules?: any[];
    infoText?: string;
    sortOptions?: (a: any, b: any) => number;
    classifierIsObject?: boolean;
    customLayout?: { labelCol?: object; wrapperCol?: object };
    disabled?: boolean;
    customObjLabel?: (value: any) => React.ReactNode;
    filter?: (value: any) => boolean;
    help?: string;
    withSystemName?: boolean;
    distinct?: boolean;
    visible?: boolean;
    disableOnMount?: boolean;
    arrayHasKey?: boolean;
    manualOptions?: any;
    className?: string;
    isFormItem?: boolean;
    hint?: string;
    translationModule?: string;
    saveLoadedData?: boolean;
    setLoadedData?: (data: any) => void;
    selectClassName?: string;
    method?: RequestMethod;
    searchKey?: string;
    translation?: string;
    localeTranslation?: boolean;

    cacheTime?: number;
    initialFilter?: any;
}

const Select: React.FC<SelectProps> = ({
    url,
    params,
    integerKey,
    dataKey,
    isResponseArray = false,
    valueKey = 'value',
    labelKey = 'label',
    incrementKey,
    mapDataEntries,
    name,
    initialFilter,
    label,
    validation = {},
    customRules,
    filter,
    help,
    sortOptions,
    classifierIsObject,
    customLayout,
    disabled,
    customObjLabel,
    distinct,
    visible = true,
    arrayHasKey = true,
    manualOptions,
    className,
    isFormItem = true,
    disableOnMount = false,
    hint,
    translationModule,
    saveLoadedData = false,
    setLoadedData,
    selectClassName,
    method = 'GET',
    searchKey = 'q',
    translation = '',
    localeTranslation = false,
    cacheTime = 0,
    ...props
}) => {
    const [options, setOptions] = useState<any>(isResponseArray ? [] : {});
    const { locale } = useLocaleContext();
    const t = useT();
    const fetchedOnceRef = useRef(false);
    const hasSearchedRef = useRef(false);
    const knownOptionsRef = useRef<Map<any, any>>(new Map());

    type CachePayload = { expiresAt: number; data: any };
    const baseCacheKey = React.useMemo(() => {
        if (!name) return null;
        const suffix = localeTranslation ? `|${locale}` : '';
        return `selectCache:${name}${suffix}`;
    }, [name, locale, localeTranslation]);

    const readCache = (): any | null => {
        if (!baseCacheKey || cacheTime <= 0) return null;
        try {
            const raw = localStorage.getItem(baseCacheKey);
            if (!raw) return null;
            const parsed: CachePayload = JSON.parse(raw);
            if (!parsed?.expiresAt || Date.now() > parsed.expiresAt) {
                localStorage.removeItem(baseCacheKey);
                return null;
            }
            return parsed.data ?? null;
        } catch {
            return null;
        }
    };

    const writeCache = (data: any) => {
        if (!baseCacheKey || cacheTime <= 0) return;
        try {
            const payload: CachePayload = {
                expiresAt: Date.now() + cacheTime,
                data,
            };
            localStorage.setItem(baseCacheKey, JSON.stringify(payload));
        } catch {
        }
    };

    useEffect(() => {
        document.querySelectorAll('.ant-select-selector input').forEach((e) => {
            e.setAttribute('autoComplete', 'noAutoComplete');
        });
    }, []);

    const parseOptions = useCallback(
        (raw: any) => {
            let parsed = raw;

            if (!parsed) parsed = isResponseArray ? [] : {};

            if (filter && Array.isArray(parsed)) {
                parsed = parsed.filter(filter);
            }

            if (mapDataEntries && Array.isArray(parsed)) {
                parsed = parsed.map(mapDataEntries);
            }

            if (distinct && Array.isArray(parsed)) {
                const map = new Map();
                parsed = parsed.filter((item) => {
                    if (map.has(item.value)) return false;
                    map.set(item.value, true);
                    return true;
                });
            }

            if (sortOptions) {
                if (classifierIsObject) {
                    parsed = Object.entries(parsed).sort(sortOptions);
                } else if (Array.isArray(parsed)) {
                    parsed = parsed.sort(sortOptions);
                }
            }

            if (translationModule && !Array.isArray(parsed)) {
                Object.keys(parsed).forEach((key) => {
                    parsed[key] = t(`${translationModule}.${key.toLowerCase()}`);
                });
            }

            return parsed;
        },
        [
            filter,
            mapDataEntries,
            distinct,
            sortOptions,
            classifierIsObject,
            translationModule,
            isResponseArray,
            t,
        ]
    );

    const initialParamsRef = useRef<Record<string, any> | null>(null);
    const hasValidInitialFilter = useRef<boolean>(false);
    const initialFilterProvided = useRef<boolean>(false);

    if (initialParamsRef.current === null) {
        initialParamsRef.current = { ...(params ?? {}) };

        initialFilterProvided.current = initialFilter !== undefined;

        const isValidFilter = initialFilter !== undefined && 
                             initialFilter !== null && 
                             initialFilter !== '' &&
                             (Array.isArray(initialFilter) ? initialFilter.length > 0 : true);
        
        if (isValidFilter) {
            hasValidInitialFilter.current = true;
            const preselectedParams: Record<string, any> = { ...initialParamsRef.current, id: initialFilter };
            delete preselectedParams.limit;
            delete preselectedParams.status;
            initialParamsRef.current = preselectedParams;
        }
    }

    const { fetch, isFetching } = useQueryApiClient({
        request: {
            url: url ?? '',
            method: method,
            data: params ?? {},
            disableOnMount: true,
        },
        onSuccess: (response): void => {
            const raw = dataKey ? response?.[dataKey] : response;
            const parsed = parseOptions(raw);

            if (isResponseArray && Array.isArray(parsed)) {
                parsed.forEach((option: any) => knownOptionsRef.current.set(option?.[valueKey], option));
            }

            setOptions(parsed);
            writeCache(parsed);

            if (saveLoadedData && setLoadedData) {
                setLoadedData(parsed);
            }
        },
    });

    useEffect(() => {
        if (manualOptions) {
            setOptions(manualOptions);
            return;
        }
        if (!url) return;

        if (!fetchedOnceRef.current) {
            const cached = readCache();
            if (cached) {
                setOptions(cached);
                if (saveLoadedData && setLoadedData) setLoadedData(cached);
                fetchedOnceRef.current = true;
                return;
            }

            if (!disableOnMount && initialParamsRef.current) {
                if (initialFilterProvided.current && !hasValidInitialFilter.current) {
                    fetchedOnceRef.current = true;
                    return;
                }
                fetch(initialParamsRef.current);
                fetchedOnceRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        url,
        manualOptions,
        disableOnMount,
    ]);

    const parsedKey = (key: string) => (integerKey ? parseInt(key, 10) : key);

    let labelCol: any = { span: 8 };
    let wrapperCol: any = { span: 6 };

    if (customLayout) {
        labelCol = customLayout.labelCol ?? undefined;
        wrapperCol = customLayout.wrapperCol ?? undefined;
    }

    const getOption = (key: any) => (customObjLabel ? customObjLabel(options[key]) : options[key]);

    const loadOptions = (input: string) => {
        if (!input) return;

        if (!hasSearchedRef.current) {
            hasSearchedRef.current = true;
        }

        const finalParams = {
            ...(params ?? {}),
            [searchKey]: input,
        };
        fetch(finalParams);
    };

    const loadOptionsTimeOut = debounce(loadOptions, 800);

    const translationLabel = (value: string): string => {
        if (localeTranslation) {
            return generalHelpers.getLocaleTextString(value, locale);
        }
        if (translation) {
            return t(`${translation}.${value.toLowerCase()}`);
        }
        return value;
    };

    const arrayOptions = (): any[] => {
        const loaded = Array.isArray(options) ? options : [];
        const selected = props.value === undefined || props.value === null
            ? []
            : (Array.isArray(props.value) ? props.value : [props.value]);

        const missing = selected
            .filter((value: any) => !loaded.some((option: any) => option?.[valueKey] === value))
            .map((value: any) => knownOptionsRef.current.get(value))
            .filter((option: any) => option !== undefined);

        return [...loaded, ...missing];
    };

    const selectComponent = (
        <AntDSelect
            loading={isFetching}
            className={`default-select ${selectClassName || ''}`}
            disabled={disabled}
            filterOption={(input, option) => {
                const label =
                    typeof option?.children === 'string'
                        ? option.children
                        : String(option?.children ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
            }}
            onSearch={loadOptionsTimeOut}
            {...props}
        >
            {isResponseArray
                ? arrayOptions().map((option: any, index: number) =>
                      arrayHasKey ? (
                          <AntDSelect.Option
                              key={incrementKey ? index : option[valueKey]}
                              value={option[valueKey]}
                          >
                              {translationLabel(option[labelKey])}
                          </AntDSelect.Option>
                      ) : (
                          <AntDSelect.Option
                              key={incrementKey ? index : option}
                              value={option[valueKey]}
                          >
                              {translationLabel(option[labelKey])}
                          </AntDSelect.Option>
                      )
                  )
                : Object.keys(options ?? {}).map((key: any, index: number) => {
                      if (sortOptions && classifierIsObject) {
                          return (
                              <AntDSelect.Option
                                  key={incrementKey ? index : parsedKey(key)}
                                  value={options[key][0]}
                              >
                                  {translationLabel(options[key][1])}
                              </AntDSelect.Option>
                          );
                      }

                      if (key === 'PHISHING_SERVICE') {
                          return (
                              <ComponentAccessCheck permission="view_phishing" key={key}>
                                  <AntDSelect.Option
                                      key={incrementKey ? index : parsedKey(key)}
                                      value={parsedKey(key)}
                                  >
                                      {translationLabel(getOption(key))}
                                  </AntDSelect.Option>
                              </ComponentAccessCheck>
                          );
                      }

                      return (
                          <AntDSelect.Option
                              key={incrementKey ? index : parsedKey(key)}
                              value={parsedKey(key)}
                          >
                              {translationLabel(getOption(key))}
                          </AntDSelect.Option>
                      );
                  })}
        </AntDSelect>
    );

    return isFormItem ? (
        <Form.Item
            className={className}
            labelCol={labelCol}
            wrapperCol={wrapperCol}
            name={name}
            label={
                hint ? (
                    <span>
                        {t(label!)}
                        <Tooltip title={t(hint)}>
                            <i className="fal fa-question-circle header-item" />
                        </Tooltip>
                    </span>
                ) : (
                    t(label!)
                )
            }
            rules={customRules || constructValidationRules(validation, t)}
            help={help}
            style={{ display: visible ? '' : 'none' }}
        >
            {selectComponent}
        </Form.Item>
    ) : (
        selectComponent
    );
};

export default Select;
